import React, { useEffect, useState } from 'react';
import {
  Save,
  Building2,
  MapPin,
  Phone,
  Image as ImageIcon,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useGetWhatsappProfileQuery } from '@/client/api/whatsapp/profile.query';
import { useUpdateWhatsappProfileMutation } from '@/client/api/whatsapp/profile.mutation';

const VERTICAL_OPTIONS = [
  { value: 'PROF_SERVICES', label: 'Professional Services' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'TRAVEL', label: 'Travel & Hospitality' },
  { value: 'HOTEL', label: 'Hotel' },
  { value: 'EVENT_PLAN', label: 'Event Planning' },
  { value: 'OTHER', label: 'Other' },
];

const BusinessProfilePage: React.FC = () => {
  const { data, isLoading, refetch } = useGetWhatsappProfileQuery();
  const updateMutation = useUpdateWhatsappProfileMutation();
  const profile = data?.data;

  const [about, setAbout] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [websitesText, setWebsitesText] = useState('');
  const [vertical, setVertical] = useState('PROF_SERVICES');

  useEffect(() => {
    if (!profile) return;
    setAbout(profile.about || '');
    setDescription(profile.description || '');
    setAddress(profile.address || '');
    setEmail(profile.email || '');
    setWebsitesText((profile.websites || []).join('\n'));
    setVertical(profile.vertical || 'PROF_SERVICES');
  }, [profile]);

  const handleSave = () => {
    const websites = websitesText
      .split('\n')
      .map((w) => w.trim())
      .filter(Boolean);

    updateMutation.mutate(
      { about, description, address, email, websites, vertical },
      {
        onSuccess: () => {
          toast.success('Business profile updated');
          refetch();
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to update business profile');
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp Business Profile</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your business information displayed on WhatsApp</p>
          </div>
          <button
            onClick={handleSave}
            disabled={isLoading || updateMutation.isPending}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">Profile Visibility</h3>
              <p className="text-sm text-blue-800">
                This information will be visible to customers when they view your WhatsApp Business profile. Keep it accurate and up-to-date.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Profile Picture
            </h2>

            <div className="flex items-center gap-6">
              {profile?.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt="WhatsApp business profile"
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center text-white text-3xl font-bold">
                  FF
                </div>
              )}
              <p className="text-xs text-gray-500">
                Profile photo updates aren't supported from the admin yet — manage the photo from WhatsApp Business Manager.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Business Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About
                </label>
                <input
                  type="text"
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  maxLength={139}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">{about.length}/139 characters — short status line shown next to your business name</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Category
                </label>
                <select
                  value={vertical}
                  onChange={(e) => setVertical(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {VERTICAL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  maxLength={256}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">{description.length}/256 characters</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Address
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Address
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Contact Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Websites (one per line, up to 2)
                </label>
                <textarea
                  value={websitesText}
                  onChange={(e) => setWebsitesText(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="https://feelyfeely.com"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfilePage;
